import { useMemo, useState } from "react";
import theme from "../../../../../theme";
import { AddressSection } from "../../../../../components/AddressSection";
import { PreviousNameSection } from "../../../../../components/PreviousNameSection";
import { NatureOfBusinessSection } from "../../../../../components/NatureOfBusinessSection";
import { Box } from "../../../../../components/Box";
import { useFillViewportHeight } from "../../../../../hooks/useFillViewportHeight";
import { Flex } from "../../../../../components/Flex";
import { DetailField } from "../../../../../components/DetailField";
import { EntityType } from "../../../../../types/CompanyType";
import { Country } from "../../../../../types/Country";
import { formatDate } from "../../../../../utils/formatters";
import { TitleHeading, Heading } from "../../../../IndividualView";
import { Slider } from "../../../../../components/Slider";
import { H3 } from "../../../../../components/Heading";
import { showSuccess, showError } from "../../../../../utils/error-handling";
import {
  EditEntityDetailsForm,
  EntityEditValues,
} from "./EditEntityDetailsForm";
import {
  EditPersonDetailsForm,
  PersonEditValues,
} from "./EditPersonDetailsForm";
import { useCompanyStatusOptions } from "../../../../../data/fallbackPayloads/useCompanyStatusOptions";
import { capitalise } from "../../../../../utils/string";
import { useAuth } from "../../../../../context/AuthContext";

const toId = (v: any): number | null => {
  if (typeof v === "number") return v;
  if (v && typeof v === "object" && typeof v.id === "number") return v.id;
  return null;
};

export const DetailsTab = ({
  profile,
  isEntity,
  entityTypes,
  countries,
  onSave,
}: {
  profile: any;
  isEntity: boolean;
  entityTypes: any;
  countries: any;
  onSave?: (payload: any) => Promise<any> | void;
}) => {
  const { ref: fillRef, height: fillHeight } = useFillViewportHeight();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { hasPermission } = useAuth();

  const entityTypeList: EntityType[] = useMemo(
    () => (Array.isArray(entityTypes) ? entityTypes : entityTypes?.data ?? []),
    [entityTypes],
  );

  const countryList: Country[] = useMemo(
    () => (Array.isArray(countries) ? countries : countries?.data ?? []),
    [countries]
  );

  const { options: companyStatusOptions } = useCompanyStatusOptions();

  const entityTypeName = useMemo(() => {
    const id = toId(profile?.entity_type);
    const found = entityTypeList.find((x) => x.id === id);
    return found?.name ?? "-";
  }, [entityTypeList, profile?.entity_type]);

  const countryName = useMemo(() => {
    const id = toId(profile?.country);
    const found = countryList.find((x) => x.id === id);
    return found?.name ?? "-";
  }, [countryList, profile?.country]);

  const statusLabel = useMemo(() => {
    const id = toId(profile?.company_status);
    if (!id) return "-";
    const found = (companyStatusOptions ?? []).find((o: any) => o.value === id);
    return found?.label ?? profile?.company_status_name ?? "-";
  }, [profile?.company_status, profile?.company_status_name, companyStatusOptions]);

  const registrationNumber = profile?.registration_number ?? "-";

  const entityTypeOptions =
    entityTypeList?.map((t: any) => ({ label: t.name, value: t.id })) ?? [];

  const countryOptions =
    countryList?.map((c: any) => ({ label: c.name, value: c.id })) ?? [];

  const initialEntityValues: EntityEditValues = {
    name: profile?.name ?? "",
    country: toId(profile?.country),
    entityType: toId(profile?.entity_type),
    status: toId(profile?.company_status) ?? null,
    registrationNumber: profile?.registration_number ?? "",
    dateOfRegistration: profile?.date_of_incorporation ?? "",
  };

  const initialPersonValues: PersonEditValues = {
    title: profile?.title ?? "",
    firstName: profile?.first_name ?? "",
    middleName: profile?.middle_name ?? "",
    lastName: profile?.last_name ?? "",
    dateOfBirth: profile?.date_of_birth ?? "",
    email: profile?.email ?? "",
    gender: profile?.gender ?? "",
  };

  const handleSubmitEntity = async (values: EntityEditValues) => {
    try {
      await onSave?.({
        profileId: Number(profile?.id),
        _type: "entity",
        name: values.name.trim(),
        entity_type: values.entityType ?? null,
        company_status: values.status ?? null,
        registration_number: values.registrationNumber.trim() || null,
        date_of_incorporation: values.dateOfRegistration || null,
        country: values.country ?? null,
      });

      showSuccess("Profile updated successfully");
      setIsEditOpen(false);
    } catch (e) {
      showError(e, "Failed to update profile");
    }
  };

  const handleSubmitPerson = async (values: PersonEditValues) => {
    try {
      await onSave?.({
        profileId: Number(profile?.id),
        _type: "individual",
        title: values.title?.trim() || null,
        first_name: values.firstName.trim(),
        middle_name: values.middleName?.trim() || null,
        last_name: values.lastName.trim(),
        date_of_birth: values.dateOfBirth || null,
        email: values.email?.trim() || null,
        gender: values.gender || null,
      });

      showSuccess("Profile updated successfully");
      setIsEditOpen(false);
    } catch (e) {
      showError(e, "Failed to update profile");
    }
  };

  return (
    <Box ref={fillRef} height={fillHeight} overflowY="auto" overflowX="hidden">
      <TitleHeading mb={2}>Profile Details</TitleHeading>

      <Box
        background={theme.colors.white}
        borderRadius={12}
        p={16}
        border={`1px solid ${theme.colors.divider}`}
      >
        <Flex justifyContent="space-between" alignItems="center" mb={2}>
          <Heading fontSize={14}>Main Details</Heading>

          <span
            style={{
              color: theme.colors.link,
              cursor: hasPermission("profile.edit") ? "pointer" : "not-allowed",
              fontWeight: 600,
              opacity: hasPermission("profile.edit") ? 1 : 0.4,
            }}
            onClick={
              hasPermission("profile.edit") ? () => setIsEditOpen(true) : undefined
            }
            title={
              hasPermission("profile.edit")
                ? undefined
                : "You don't have permission to edit this profile"
            }
          >
            Edit
          </span>
        </Flex>

        {isEntity ? (
          <>
            <Flex gap="24">
              <DetailField flex={1} label="Name" value={profile?.name} />
              <DetailField flex={1} label="Entity Type" value={entityTypeName} />
              <DetailField
                flex={1}
                label="Registration Number"
                value={registrationNumber}
              />
              <DetailField flex={1} label="Status" value={statusLabel} />
            </Flex>

            <Flex gap="24" mt={2}>
              <DetailField flex={1} label="Country" value={countryName} />
              <DetailField
                flex={1}
                label="Date of Registration"
                value={formatDate(profile?.date_of_incorporation)}
              />
              <Box flex={1} />
              <Box flex={1} />
            </Flex>
          </>
        ) : (
          <>
            <Flex gap="24">
              <DetailField flex={1} label="Title" value={profile?.title} />
              <DetailField
                flex={1}
                label="First name"
                value={profile?.first_name}
              />
              <DetailField
                flex={1}
                label="Middle name"
                value={profile?.middle_name}
              />
              <DetailField
                flex={1}
                label="Last name"
                value={profile?.last_name}
              />
            </Flex>

            <Flex gap="24" mt={2}>
              <DetailField
                flex={1}
                label="Date of birth"
                value={formatDate(profile?.date_of_birth)}
              />
              <DetailField flex={1} label="Email" value={profile?.email} />
              <DetailField
                flex={1}
                label="Gender"
                value={capitalise(profile?.gender)}
              />
              <Box flex={1} />
            </Flex>
          </>
        )}
      </Box>

      <AddressSection profile={profile} />

      {isEntity && (
        <>
          <PreviousNameSection profile={profile} />

          <NatureOfBusinessSection profile={profile} />
        </>
      )}

      <Slider
        isOpen={isEditOpen}
        onRequestClose={() => setIsEditOpen(false)}
        width="600px"
      >
        <Box p={3}>
          <Flex justifyContent="space-between" alignItems="center" mb={3}>
            <H3 fontSize={16}>
              {isEntity ? "Edit Entity Details" : "Edit Person Details"}
            </H3>
          </Flex>

          {isEntity ? (
            <EditEntityDetailsForm
              initialValues={initialEntityValues}
              entityTypeOptions={entityTypeOptions}
              countryOptions={countryOptions}
              statusOptions={companyStatusOptions as any}
              onCancel={() => setIsEditOpen(false)}
              onSubmit={handleSubmitEntity}
            />
          ) : (
            <EditPersonDetailsForm
              initialValues={initialPersonValues}
              onCancel={() => setIsEditOpen(false)}
              onSubmit={handleSubmitPerson}
            />
          )}
        </Box>
      </Slider>
    </Box>
  );
};
